import { useNavigate } from '@tanstack/react-router';
import type { RecentlyTouched } from '@imza/shared';
import { Skeleton } from '../ui/Skeleton.js';
import { Phone, MessageSquare, FileText, TrendingUp, Clock } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';

const STAGE_LABEL: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  interested: 'Interested',
  not_interested: 'Not interested',
  follow_up: 'Follow-up',
  potential: 'Potential',
  sold: 'Sold',
  rented: 'Rented',
};

const STAGE_COLOR: Record<string, string> = {
  new: 'text-secondary bg-border/50',
  contacted: 'text-blue-700 bg-blue-50',
  interested: 'text-amber-700 bg-amber-50',
  not_interested: 'text-red-700 bg-red-50',
  follow_up: 'text-purple-700 bg-purple-50',
  potential: 'text-orange-700 bg-orange-50',
  sold: 'text-green-700 bg-green-50',
  rented: 'text-teal-700 bg-teal-50',
};

function ActivityIcon({ type }: { type: string }) {
  switch (type) {
    case 'call':
      return <Phone className="w-3.5 h-3.5" aria-hidden="true" />;
    case 'whatsapp':
      return <MessageSquare className="w-3.5 h-3.5" aria-hidden="true" />;
    case 'stage_change':
      return <TrendingUp className="w-3.5 h-3.5" aria-hidden="true" />;
    case 'note':
      return <FileText className="w-3.5 h-3.5" aria-hidden="true" />;
    default:
      return <Clock className="w-3.5 h-3.5" aria-hidden="true" />;
  }
}

const TYPE_COLOR: Record<string, string> = {
  call: 'text-blue-600 bg-blue-50',
  whatsapp: 'text-green-600 bg-green-50',
  stage_change: 'text-purple-600 bg-purple-50',
  note: 'text-amber-600 bg-amber-50',
};

interface Props {
  items: RecentlyTouched[];
  isLoading: boolean;
}

export function RecentTouchesFeed({ items, isLoading }: Props) {
  const navigate = useNavigate();

  return (
    <div className="bg-white border border-border rounded-xl p-4">
      <p className="text-sm font-semibold text-primary-text mb-3">Recently touched</p>
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-secondary text-center py-4">No recent activity</p>
      ) : (
        <ul className="divide-y divide-border -mx-4">
          {items.map((item) => (
            <li key={`${item.landlordId}-${item.lastActivityAt}`}>
              <button
                onClick={() => navigate({ to: '/landlords/$id' as any, params: { id: String(item.landlordId) } as any })}
                className="w-full text-left px-4 py-2.5 min-h-[44px] hover:bg-hover-bg transition-colors flex items-start gap-3"
                aria-label={`${item.landlordName} — ${item.lastActivityType}`}
              >
                {/* Activity type icon */}
                <span
                  className={`flex-shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center ${TYPE_COLOR[item.lastActivityType] ?? 'text-secondary bg-border/50'}`}
                  aria-hidden="true"
                >
                  <ActivityIcon type={item.lastActivityType} />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-primary-text truncate">{item.landlordName}</p>
                    <span className="text-[10px] text-secondary whitespace-nowrap flex-shrink-0">
                      {formatDistanceToNowStrict(new Date(item.lastActivityAt), { addSuffix: true })}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {item.areaName && (
                      <span className="text-xs text-secondary truncate max-w-[120px]">{item.areaName}</span>
                    )}
                    {item.building && (
                      <>
                        <span className="text-secondary/40 text-xs">·</span>
                        <span className="text-xs text-secondary truncate max-w-[120px]">{item.building}</span>
                      </>
                    )}
                    <span
                      className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${STAGE_COLOR[item.stage] ?? 'text-secondary bg-border/50'}`}
                    >
                      {STAGE_LABEL[item.stage] ?? item.stage}
                    </span>
                  </div>
                  {item.agentName && (
                    <p className="text-[10px] text-secondary mt-0.5">{item.agentName}</p>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
